import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { Reservation, House } from '../../services/types';
import { isValidImageFile, formatFileSize } from '../../utils/imageCompression';

interface PhotoUploadModalProps {
    show: boolean;
    onHide: () => void;
    reservations: Reservation[];
    houses: House[];
    onUpload: (data: {
        reservation_id: string;
        type: 'check-in' | 'check-out';
        files: File[];
    }) => Promise<void>;
    preSelectedReservationId?: string;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
    show,
    onHide,
    reservations,
    houses,
    onUpload,
    preSelectedReservationId,
}) => {
    const [reservationId, setReservationId] = useState('');
    const [photoType, setPhotoType] = useState<'check-in' | 'check-out'>('check-in');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Debug logging - this runs whenever reservations or houses props change
    React.useEffect(() => {
        console.log('PhotoUploadModal - Props updated!');
        console.log('PhotoUploadModal - Reservations:', reservations);
        console.log('PhotoUploadModal - Reservations count:', reservations.length);
        console.log('PhotoUploadModal - Houses:', houses);
        console.log('PhotoUploadModal - Houses count:', houses.length);
        console.log('PhotoUploadModal - show:', show);
    }, [reservations, houses, show]);

    // Auto-select reservation when modal opens with preSelectedReservationId
    React.useEffect(() => {
        if (show && preSelectedReservationId) {
            setReservationId(preSelectedReservationId);
        }
    }, [show, preSelectedReservationId]);

    // Get house name for the selected reservation
    const getHouseName = (houseId: string) => {
        const house = houses.find((h) => h.public_id === houseId);
        return house ? house.name || house.address : 'Unknown';
    };

    // Get house ID from selected reservation
    const selectedReservation = reservations.find((r) => r.public_id === reservationId);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setError('');

        // Validate files
        const invalidFiles = files.filter((file) => !isValidImageFile(file));
        if (invalidFiles.length > 0) {
            setError('Please select only image files (JPEG, PNG, WebP, GIF)');
            return;
        }

        // Add new files to existing selection
        const newFiles = [...selectedFiles, ...files];
        setSelectedFiles(newFiles);

        // Generate previews
        const newPreviews = files.map((file) => URL.createObjectURL(file));
        setPreviews([...previews, ...newPreviews]);
    };

    const handleRemoveFile = (index: number) => {
        // Revoke object URL to prevent memory leaks
        URL.revokeObjectURL(previews[index]);

        // Remove file and preview
        setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
        setPreviews(previews.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        setError('');

        // Validation
        if (!reservationId) {
            setError('Please select a reservation');
            return;
        }

        if (selectedFiles.length === 0) {
            setError('Please select at least one photo');
            return;
        }

        try {
            setUploading(true);
            await onUpload({
                reservation_id: reservationId,
                type: photoType,
                files: selectedFiles,
            });

            // Reset form
            handleClose();
        } catch (err: any) {
            setError(err.message || 'Failed to upload photos');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        // Clean up object URLs
        previews.forEach((preview) => URL.revokeObjectURL(preview));

        // Reset state
        setReservationId('');
        setPhotoType('check-in');
        setSelectedFiles([]);
        setPreviews([]);
        setError('');
        setUploading(false);

        onHide();
    };

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const isMobile = window.innerWidth < 768;

    return (
        <Modal show={show} onHide={handleClose} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                    <Upload size={isMobile ? 20 : 24} className="me-2" />
                    Upload Photos
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        <AlertCircle size={18} className="me-2" />
                        {error}
                    </Alert>
                )}

                <Form>
                    {/* No Reservations Warning */}
                    {reservations.length === 0 && (
                        <Alert variant="warning" className="mb-3">
                            <AlertCircle size={18} className="me-2" />
                            No reservations available. Please create a reservation first before uploading photos.
                        </Alert>
                    )}

                    {/* Reservation Selection */}
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Reservation <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            value={reservationId}
                            onChange={(e) => setReservationId(e.target.value)}
                            disabled={uploading || reservations.length === 0}
                        >
                            <option value="">
                                {reservations.length === 0
                                    ? 'No reservations available'
                                    : 'Select a reservation'}
                            </option>
                            {reservations.map((reservation) => (
                                <option key={reservation.public_id} value={reservation.public_id}>
                                    {reservation.name_first_guess || 'Guest'} -{' '}
                                    {getHouseName(reservation.house_id)} ({reservation.check_in_date} to{' '}
                                    {reservation.check_out_date})
                                </option>
                            ))}
                        </Form.Select>
                        {reservations.length === 0 && (
                            <Form.Text className="text-muted">
                                Go to Reservations page to create a new reservation.
                            </Form.Text>
                        )}
                        {preSelectedReservationId && reservationId === preSelectedReservationId && (
                            <Form.Text className="text-success">
                                Reservation automatically selected from detail view
                            </Form.Text>
                        )}
                    </Form.Group>

                    {/* Photo Type Selection */}
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Photo Type <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex gap-3">
                            <Form.Check
                                type="radio"
                                label="Check-in"
                                name="photoType"
                                value="check-in"
                                checked={photoType === 'check-in'}
                                onChange={(e) => setPhotoType(e.target.value as 'check-in')}
                                disabled={uploading}
                            />
                            <Form.Check
                                type="radio"
                                label="Check-out"
                                name="photoType"
                                value="check-out"
                                checked={photoType === 'check-out'}
                                onChange={(e) => setPhotoType(e.target.value as 'check-out')}
                                disabled={uploading}
                            />
                        </div>
                    </Form.Group>

                    {/* File Upload */}
                    <Form.Group className="mb-3">
                        <Form.Label>Photos</Form.Label>
                        <div
                            className="border rounded p-4 text-center"
                            style={{
                                borderStyle: 'dashed',
                                borderColor: '#dee2e6',
                                backgroundColor: '#f8f9fa',
                                cursor: uploading ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => !uploading && fileInputRef.current?.click()}
                        >
                            <ImageIcon size={48} className="text-muted mb-2" />
                            <p className="mb-2">Click to select photos or drag and drop</p>
                            <p className="text-muted small mb-0">
                                Supports: JPEG, PNG, WebP, GIF (Max 2MB each after compression)
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                                disabled={uploading}
                            />
                        </div>
                    </Form.Group>

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold">
                                    Selected Photos ({selectedFiles.length})
                                </span>
                                <Badge bg="info">{formatFileSize(totalSize)}</Badge>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                {selectedFiles.map((file, index) => (
                                    <div
                                        key={index}
                                        className="position-relative"
                                        style={{
                                            width: '100px',
                                            height: '100px',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            border: '2px solid #dee2e6',
                                        }}
                                    >
                                        <img
                                            src={previews[index]}
                                            alt={file.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-1 p-1"
                                            onClick={() => handleRemoveFile(index)}
                                            disabled={uploading}
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                padding: 0,
                                            }}
                                        >
                                            <X size={14} />
                                        </Button>
                                        <div
                                            className="position-absolute bottom-0 w-100 text-center text-white small"
                                            style={{
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                padding: '2px',
                                                fontSize: '0.65rem',
                                            }}
                                        >
                                            {formatFileSize(file.size)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={uploading || reservations.length === 0 || selectedFiles.length === 0}
                >
                    {uploading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <Upload size={18} className="me-2" />
                            Upload {selectedFiles.length > 0 && `(${selectedFiles.length})`}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PhotoUploadModal;